import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const normalizeString = (str) => {
  return str
    ? str
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    : '';
};

const formatUpperName = (value) => {
  const upper = (value || '').toLocaleUpperCase('tr-TR');
  return upper.replace(/[^A-ZÇĞİÖŞÜ\s]/g, '').replace(/\s{2,}/g, ' ');
};

const formatPhoneNumber = (value) => {
  const raw = (value || '').replace(/[^\d]/g, '');
  let local = raw;
  if (local.startsWith('90')) {
    local = local.slice(2);
  }
  local = local.slice(0, 10);

  const p1 = local.slice(0, 3);
  const p2 = local.slice(3, 6);
  const p3 = local.slice(6, 8);
  const p4 = local.slice(8, 10);

  let formatted = '+90';
  if (p1) formatted += ` ${p1}`;
  if (p2) formatted += ` ${p2}`;
  if (p3) formatted += ` ${p3}`;
  if (p4) formatted += ` ${p4}`;
  if (!p1) formatted += ' ';
  return formatted;
};

const splitAdSoyad = (adSoyad) => {
  const trimmed = (adSoyad || '').trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) {
    return { isim: trimmed, soyisim: '' };
  }
  return {
    isim: trimmed.substring(0, spaceIndex).trim(),
    soyisim: trimmed.substring(spaceIndex + 1).trim(),
  };
};

function QuickCustomerAddModal({ open, onClose, sube, onSuccess }) {
  const [adSoyad, setAdSoyad] = useState('');
  const [telefon, setTelefon] = useState('+90 ');
  const [tcKimlikNo, setTcKimlikNo] = useState('');
  const [errors, setErrors] = useState({});
  const [hata, setHata] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);

  useEffect(() => {
    if (!open) {
      setAdSoyad('');
      setTelefon('+90 ');
      setTcKimlikNo('');
      setErrors({});
      setHata('');
      setKaydediliyor(false);
    }
  }, [open]);

  const validateForm = async () => {
    const newErrors = {};
    const trimmedAd = adSoyad.trim();

    if (!trimmedAd) {
      newErrors.adSoyad = 'Ad Soyad / Ünvan zorunlu';
    }

    if (telefon && telefon.trim() !== '+90' && !/^\+90\s\d{3}\s\d{3}\s\d{2}\s\d{2}$/.test(telefon)) {
      newErrors.telefon = 'Telefon formatı +90 5xx xxx xx xx olmalı';
    }

    if (tcKimlikNo && !/^\d{11}$/.test(tcKimlikNo)) {
      newErrors.tcKimlikNo = 'TC Kimlik No 11 haneli olmalı';
    }

    if (trimmedAd) {
      const normalizedMusteriId = normalizeString(trimmedAd);
      if (!normalizedMusteriId) {
        newErrors.adSoyad = 'Geçerli bir ad girin';
      } else {
        const musteriRef = doc(db, 'subeler', sube, 'musteriler', normalizedMusteriId);
        const musteriSnap = await getDoc(musteriRef);
        if (musteriSnap.exists()) {
          newErrors.adSoyad = 'Bu müşteri zaten kayıtlı';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setHata('');
    if (!(await validateForm())) return;

    setKaydediliyor(true);
    try {
      const trimmedAd = adSoyad.trim();
      const { isim, soyisim } = splitAdSoyad(trimmedAd);
      const normalizedMusteriId = normalizeString(trimmedAd);
      const iletisimNo = telefon.trim() === '+90' ? null : telefon.trim() || null;

      const musteriRef = doc(db, 'subeler', sube, 'musteriler', normalizedMusteriId);
      await setDoc(musteriRef, {
        isim,
        soyisim,
        tcKimlikNo: tcKimlikNo || null,
        iletisimNo,
        adres: null,
        bilgiler: null,
        timestamp: new Date(),
        sube,
      });

      const newMusteri = {
        id: normalizedMusteriId,
        isim,
        soyisim,
        ad: `${isim} ${soyisim}`.trim(),
        tcKimlikNo: tcKimlikNo || null,
        iletisimNo,
      };

      onSuccess(newMusteri);
    } catch (err) {
      setHata(`Müşteri eklenemedi: ${err.message}`);
      console.error('Firestore Hatası:', err);
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          m: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontSize: { xs: '18px', sm: '20px' },
          fontWeight: 700,
          color: '#102a43',
        }}
      >
        Yeni Müşteri Ekle
        <IconButton onClick={onClose} size="small" aria-label="Kapat">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Ad Soyad / Ünvan"
            required
            fullWidth
            size="small"
            value={adSoyad}
            onChange={(e) => setAdSoyad(formatUpperName(e.target.value))}
            error={!!errors.adSoyad}
            helperText={errors.adSoyad}
            autoFocus
            inputProps={{ style: { fontSize: '16px' } }}
          />
          <TextField
            label="Telefon"
            fullWidth
            size="small"
            value={telefon}
            onChange={(e) => setTelefon(formatPhoneNumber(e.target.value))}
            placeholder="+90 5xx xxx xx xx"
            error={!!errors.telefon}
            helperText={errors.telefon}
            inputProps={{ style: { fontSize: '16px' } }}
          />
          <TextField
            label="TC Kimlik Numarası"
            fullWidth
            size="small"
            value={tcKimlikNo}
            onChange={(e) => setTcKimlikNo((e.target.value || '').replace(/\D/g, '').slice(0, 11))}
            placeholder="11 haneli TC Kimlik No"
            error={!!errors.tcKimlikNo}
            helperText={errors.tcKimlikNo}
            inputProps={{
              style: { fontSize: '16px' },
              inputMode: 'numeric',
              maxLength: 11,
            }}
          />
          {hata && (
            <Typography
              variant="body2"
              sx={{
                color: '#b91c1c',
                bgcolor: '#fee2e2',
                border: '1px solid #fecaca',
                p: 1,
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              {hata}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={kaydediliyor}>
          İptal
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={kaydediliyor}
          sx={{
            bgcolor: '#a8671d',
            '&:hover': { bgcolor: '#955d18' },
          }}
        >
          {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickCustomerAddModal;
